/* eslint-disable new-cap */
import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecord } from '@deity-io/di-data-basics';
import type { Component, Flow } from '@deity-io/di-data-flows';
import { FlowLoader } from '@deity-io/di-data-flows';
import type { IEditorConfig, IValidationMessage, IValidationResult } from '@deity-io/di-data-flows-editor';
import { ComponentFactory } from '@deity-io/di-data-flows-editor';
import type { mxCell, mxGraph, mxKeyHandler, mxPoint } from '@deity-io/mxgraph';
import { Box, Button, FlexLayout, H1, H3 } from '@deity-io/portal-ui';
import type { IConnectPayload } from 'contexts/FlowContext';
import { FlowActionTypes, FlowContext } from 'contexts/FlowContext';
import { UIContext } from 'contexts/UIContext';
import { CACHE_FIRST, EDITOR_PATH_ID, ENTITY_PATH, NETWORK_FIRST, OUTPUT_PORT_COMPONENT_ID } from 'contracts/constants';
import type { IPoint } from 'contracts/interfaces';
import _, { get, isEqual } from 'lodash';
import type { FC } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
// <- import types only
import { Prompt, useLocation } from 'react-router-dom';
import {
  useGetEndpointsByPackageVersionQuery,
  useGetFlowQuery,
  useGetFlowsByPackageVersionQuery,
  useGetMappingsByPackageVersionQuery,
  useGetModelsByPackageVersionQuery,
  useGetPackageVersionQuery
} from 'services/graphql/generated/hooks';
import { throttle } from 'services/mxGraph';
import mx from 'services/mxGraph/mx'; // <- import values from factory()
import { getParams } from 'utils';
import { isPackageEditable } from 'utils/is-package-editable';
import { v4 as uuidv4 } from 'uuid';
import FlowPanel from './components/panels/FlowPanel';
import { findDOMParent, initGraph, menuFactory } from './editorBootstrap';
import {
  COMP_ID_TRIGGER,
  canConnect,
  connectPayload,
  getComponentBounds,
  isVirtualComponent
} from './helpers/flowHelpers';
import { addPorts } from './helpers/mxGraph/addPorts';
import { tooltipBuilder } from './helpers/mxGraph/tooltipBuilder';
import { virtualSettings } from './helpers/virtualComponents';

interface FlowEditorProps {
  isEditable: boolean;
}
const FlowEditor: FC<FlowEditorProps> = ({ isEditable }) => {
  const DEFAULT_TRIGGER_LOCATION = { x: 100, y: 100 };
  const { pathname } = useLocation();
  const ctx = useContext(FlowContext);
  const ctxUI = useContext(UIContext);
  const editorContainer = useRef<any>(null);
  const graph = useRef<mxGraph & { lazyZoom?: any; mappingData?: any; endpointsData?: any; modelsData?: any }>();
  const iniRender = useRef<boolean>(false);
  const keyHandlerRef = useRef<mxKeyHandler>();
  const parentRef: any = useRef();
  const [renderError, setRenderError] = useState<string | undefined>(undefined);
  const virtualComponents: {
    [name: string]: {
      style: string;
      input: boolean;
      name: string;
      validate?: (component: Component, port?: string, output?: string) => [boolean, Array<string>];
      connect?: () => void;
    };
  } = {
    trigger: {
      style: 'trigger',
      input: false,
      name: 'Trigger',
      validate: (component: Component) => ctx?.state.flow?.connectTrigger(component, true)!
    },
    tail: {
      style: 'tail',
      input: true,
      name: 'Tail',
      validate: (component: Component, port?: string, output?: string) =>
        ctx?.state.flow?.connectTail(component, port || 'next', output || 'tail', true)!
    }
  };
  const params: any = getParams(pathname, [EDITOR_PATH_ID, ENTITY_PATH]);
  const { data: packageVersion } = useGetPackageVersionQuery({
    ...CACHE_FIRST,
    variables: {
      uuid: params.versionUuid
    },
    skip: !params.versionUuid
  });

  const { data } = useGetFlowQuery({
    ...NETWORK_FIRST,
    variables: {
      packageVersionUuid: params.versionUuid,
      uuid: params.entityId
    },
    skip: !(params.versionUuid && params.entityId)
  });

  const variables = {
    offset: 0,
    limit: 500,
    packageVersionUuid: params.versionUuid
  };

  const { data: mappingsData } = useGetMappingsByPackageVersionQuery({
    variables,
    ...NETWORK_FIRST,
    skip: !params.versionUuid
  });

  const { data: endpointsData } = useGetEndpointsByPackageVersionQuery({
    variables,
    ...NETWORK_FIRST,
    skip: !params.versionUuid
  });

  const { data: modelsData } = useGetModelsByPackageVersionQuery({
    variables: { ...variables, expand: true },
    ...NETWORK_FIRST,
    skip: !params.versionUuid
  });

  const { data: flowsData } = useGetFlowsByPackageVersionQuery({
    variables,
    ...NETWORK_FIRST,
    skip: !params.versionUuid
  });

  const setSelection = () => {
    if (graph.current) {
      if (ctx?.state.selectedComponent) {
        if (
          'componentId' in (ctx?.state.selectedComponent || {}) &&
          !('port' in (ctx?.state.selectedComponent || { port: '' }))
        ) {
          // @ts-ignore
          const compId = ctx.state.selectedComponent.componentId;
          const found = graph.current
            ?.getSelectionCells()
            .find(selectionCell => selectionCell.value?.compId === compId);
          if (!found) {
            const compCell = findComponent(compId);
            if (compCell) {
              graph.current?.setSelectionCell(compCell);
            }
          }
        }
      }
    }
  };

  const connectionCB = (sender, evt) => {
    const edge = evt.getProperty('cell');
    if (isVirtualComponent(edge.source.parent) && edge.source.parent.virtualComponentType === 'trigger') {
      const settings = virtualComponents[edge.source.parent.virtualComponentType];
      ctx?.dispatch({
        type: FlowActionTypes.CONNECT_TRIGGER,
        payload: {
          to: { compId: edge.target.parent.value.compId, portKey: edge.target.value.key }
        }
      });

      return;
    }
    if (isVirtualComponent(edge.target.parent) && edge.target.parent.virtualComponentType === 'tail') {
      const settings = virtualComponents[edge.target.parent.virtualComponentType];

      ctx?.dispatch({
        type: FlowActionTypes.CONNECT_TAIL,
        payload: {
          from: { compId: edge.source.parent.value.compId, portKey: edge.source.value.key },
          tail: edge.target.parent.value.compId
        }
      });

      return;
    }
    const payload: IConnectPayload | undefined = connectPayload(edge.source, edge.target);
    if (payload) {
      ctx?.dispatch({
        type: FlowActionTypes.CONNECT,
        payload
      });
    }
  };

  /**
   * Called by graph. will color edge invalid if message returned.
   * last Message returned will be popped up (internal graph call, can be overwritten) when connection was attempted
   */
  const validateEdge = (edge: mxCell, source: mxCell, target: mxCell): string | null => {
    if (isVirtualComponent(source.parent)) {
      const compo = ctx?.state.flow?.getComponent(target.parent.value.compId);
      if (compo && source.parent.virtualComponentType === 'trigger') {
        // @todo skip validation for now for trigger and tail
        const settings = virtualComponents[source.parent.virtualComponentType];
        const [isValid, messages] = settings.validate!(compo, source.value.key);
        if (!isValid) {
          return messages.join('.');
        }
      } else {
        return `Unable to connect`;
      }

      return null;
    }
    if (isVirtualComponent(target.parent)) {
      const compo = ctx?.state.flow?.getComponent(source.parent.value.compId);
      if (compo && target.parent.virtualComponentType === 'tail') {
        const settings = virtualComponents[target.parent.virtualComponentType];
        const [isValid, messages] = settings.validate!(compo, source.value.key, target.value.key);
        if (!isValid) {
          return messages.join('.');
        }
      } else {
        return `Unable to connect`;
      }

      return null;
    }
    const payload: IConnectPayload | undefined = connectPayload(source, target);
    if (ctx?.state.flow && payload) {
      try {
        const [isValid, messages] = canConnect(ctx.state.flow, payload);
        if (!isValid) {
          return messages.join('.');
        }
      } catch (err) {
        return `Error occurred validating connection. Message: "${err}"`;
      }
    }

    return null;
  };

  /**
   * Render detailed description for the tooltips
   * @param cell
   * @returns
   */
  const getTooltipForCell = cell => {
    if (packageVersion && ctx && ctxUI) {
      return tooltipBuilder(cell, packageVersion.getPackageVersion, ctx, ctxUI!);
    }
  };

  if (graph?.current) {
    // Need to set this on every render else we have the wrong context
    graph.current.validateEdge = validateEdge;
    graph.current.popupMenuHandler.factoryMethod = (menu, cell, event) =>
      menuFactory(menu, cell, event, graph, ctx, isEditable);

    graph.current.getTooltipForCell = getTooltipForCell;

    // Bind to Keypress. Currently only the delete Key (46)
    if (keyHandlerRef.current) {
      // needed to have a valid ctx
      keyHandlerRef.current.destroy();
    }
    keyHandlerRef.current = new mx.mxKeyHandler(graph.current);
    keyHandlerRef.current.bindKey(46, (evt: KeyboardEvent) => {
      if (isEditable) {
        ctx?.dispatch({
          type: FlowActionTypes.REMOVE_SELECTED
        });
      }
    });
  }

  /**
   * find a component vertex with the matching componentId
   * @param compId componentId to find
   * @param group optional parent
   * @returns mxCell | undefined
   */
  const findComponent = (compId: string, group?: mxCell): mxCell | undefined =>
    graph.current
      ?.getChildCells(group || graph.current.getDefaultParent(), false)
      .find(cell => cell.value?.compId === compId);

  /**
   * find a component vertex with the matching componentId
   * @param compId componentId to find
   * @param group optional parent
   * @returns mxCell | undefined
   */
  const findComponentRecursive = (compId: string, group?: mxCell): mxCell | undefined => {
    let output = graph.current
      ?.getChildCells(group || graph.current.getDefaultParent(), false)
      .find(cell => cell.value?.compId === compId);
    if (!output) {
      const groups = graph.current
        ?.getChildCells(group || graph.current.getDefaultParent(), false)
        .filter(cell => cell.isGroup === true);
      if (groups && groups.length) {
        groups.every(groupItem => {
          output = findComponentRecursive(compId, groupItem);

          return output === undefined;
        });
      }
    }

    return output;
  };

  const getAllComponents = (parent?: mxCell): Array<mxCell> | undefined =>
    graph.current
      ?.getChildCells(parent || graph.current.getDefaultParent(), false)
      .filter(cell => cell.value && 'compId' in cell.value && !isVirtualComponent(cell));

  /**
   * Bring up and tear down the graph component
   */
  useEffect(() => {
    if (!iniRender.current) {
      initGraph(graph, parentRef, editorContainer, ctx, isEditable);
      graph.current!.connectionHandler.addListener(mx.mxEvent.CONNECT, (sender, evt) => connectionCB(sender, evt));
      graph.current!.addListener(mx.mxEvent.CLICK, (sender, evt) => {
        /**
         * This is an event listener for all the HTML elements form the components drawn with labelBuilder.
         */
        const src = findDOMParent(evt.properties?.event?.srcElement, 'SPAN', 5);
        if (src && src.classList?.contains('collapse')) {
          // change component to swimlane
          ctx?.dispatch({
            type: FlowActionTypes.TOGGLE_COLLAPSED,
            payload: {
              componentId: evt.properties.cell.value?.compId,
              collapsed: evt.properties.cell.style !== 'vertex'
            }
          });
          // const fold = !graph.current!.isCellCollapsed(evt.properties?.cell);
          // graph.current!.foldCells(fold, false, [evt.properties?.cell]);
        } else if (evt.properties?.event?.srcElement.classList?.contains('component__view')) {
          // If the View link is pressed
          ctx?.dispatch({
            type: FlowActionTypes.TOGGLE_PANEL,
            payload: 'CONFIG_COMPONENT'
          });
        }
      });

      graph.current!.validateEdge = validateEdge;
      graph.current!.popupMenuHandler.factoryMethod = (menu, cell, event) =>
        menuFactory(menu, cell, event, graph, ctx, isEditable);

      graph.current!.getTooltipForCell = getTooltipForCell;
    }
    iniRender.current = true;

    // Specify how to clean up after this effect:
    return function cleanup() {
      graph.current?.destroy();
      ctx?.dispatch({
        type: FlowActionTypes.CLEAR
      });
    };
  }, []);

  /**
   * Once all reference data is loaded we can Initialize the context
   */
  useEffect(() => {
    if (data && mappingsData && endpointsData && modelsData && packageVersion && flowsData) {
      ctx?.dispatch({
        type: FlowActionTypes.INIT_FLOW,
        payload: {
          uuid: data.getFlow.uuid,
          schema: data.getFlow.schema,
          endpoints: endpointsData.getEndpointsByPackageVersion.data,
          mappings: mappingsData.getMappingsByPackageVersion.data,
          models: modelsData.getModelsByPackageVersion.data,
          flows: flowsData.getFlowsByPackageVersion.data,
          locked: !isPackageEditable(packageVersion.getPackageVersion.status)
        }
      });
    }
  }, [data, mappingsData, endpointsData, modelsData, flowsData]);

  /**
   * On change of StructureHash
   */
  useEffect(() => {
    if (!graph.current?.model) {
      return;
    }
    graph.current.model.beginUpdate();

    if (!ctx?.state.selectedComponent && !graph.current.isSelectionEmpty()) {
      // Fix when selection is cleared in Reducer, but still selected in graph,
      // had to switch selection before selectionModelCb is called
      graph.current.clearSelection();
    }

    try {
      if (ctx?.state.flow) {
        const { flow } = ctx.state;
        const defaultParent = graph.current?.getDefaultParent();
        renderGraph(flow, defaultParent);
        renderEdges(flow, defaultParent);
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error(err);
      }
      setRenderError(`${err}`);
    } finally {
      graph.current.model.endUpdate();
      // graph.current.refresh();
    }
  }, [ctx?.state.structureHash]);

  /**
   * Hook on update state.flowSummary
   */
  useEffect(() => {
    if (!(graph.current?.model && ctx)) {
      return;
    }
    // model.current.beginUpdate();
    graph.current.model.beginUpdate();
    try {
      const allCells = getAllComponents();
      allCells?.forEach(cell => {
        const component = ctx.state.flow?.getComponent(cell.value.compId);
        if (component) {
          updateCellErrors(cell, component, ctx.state.flowSummary!);
        }
      });
    } finally {
      graph.current.model.endUpdate();
      graph.current.refresh();
    }
  }, [ctx?.state.flowSummary]);

  /**
   * Create a mxCell for a normal component
   */
  const createObject = (
    value: any,
    compId,
    alternateParent?: mxCell,
    offset: IPoint = { x: 0, y: 0 },
    style: string = 'vertex'
  ): mxCell => {
    const vertex = graph.current!.createVertex(
      alternateParent || graph.current!.getDefaultParent(),
      null,
      { ...value, compId },
      Number.parseInt(value.editor?.location.x, 10) - (offset?.x || 0),
      Number.parseInt(value.editor?.location.y, 10) - (offset?.y || 0),
      Number.parseInt(value.dimensions?.width, 10),
      Number.parseInt(value.dimensions?.height, 10),
      style
    );
    vertex[value.type] = true;
    vertex.vertex = true;
    vertex.setConnectable(false);

    return vertex;
  };

  /**
   * Create a mxCell for a group or subFlow component
   */
  const createSwimlane = (
    component: any,
    compId: string,
    alternateParent?: mxCell,
    // eslint-disable-next-line default-param-last
    offset: IPoint & { width?: number; height?: number } = { x: 0, y: 0 },
    cellSettings?: {
      isLocked: boolean;
      isContentLocked: boolean;
      isEdgesBound: boolean;
      isCustomExpand: boolean;
    },
    style: string = 'swimlane'
  ): mxCell => {
    const settings = (component.editor as IEditorConfig) || {};
    const cell = graph.current!.createVertex(
      alternateParent || graph.current!.getDefaultParent(),
      null,
      { ...component, compId },
      (settings.location?.x || 0) - offset.x,
      (settings.location?.y || 0) - offset.y,
      settings.size?.width || offset.width || 300,
      settings.size?.height || offset.height || 200,
      style
    );
    cell.collapsed = false;
    cell.isGroup = true; // used for recursive search of components
    // 'shape=swimlane;startSize=20;'
    cell[component.type] = true;

    /**
     * alternateBounds are in collapsed state
     */
    cell.geometry.alternateBounds = new mx.mxRectangle(
      component.editor.location.x,
      component.editor.location.y,
      Number.parseInt(component.dimensions.width, 10),
      Number.parseInt(component.dimensions.height, 10)
    );
    cell.setConnectable(false);
    if (cellSettings) {
      mapRecord(cellSettings, (value, key) => {
        cell[key] = value;

        return undefined;
      });
    }

    return cell;
  };

  const renderVirtualComponent = (
    type: 'tail' | 'trigger',
    compId: string,
    editorConfig: IEditorConfig,
    parentCell: mxCell,
    offset: IPoint
  ) => {
    let vertex = findComponentRecursive(compId);
    const { style, input: isInput, name } = virtualComponents[type];
    const { settings, ports } = virtualSettings(name, editorConfig, isInput);
    const virtualConfigPath: Array<string> = [];
    if (type === 'trigger') {
      virtualConfigPath.push('editor');
      virtualConfigPath.push('trigger');
    } else if (type === 'tail') {
      virtualConfigPath.push('outputs');
      virtualConfigPath.push(compId);
      virtualConfigPath.push('editor');
    } else {
      // eslint-disable-next-line no-console
      console.warn(`unknown virtual component`);
    }

    if (vertex) {
      vertex.value = { ...settings, compId, virtualConfigPath };
      vertex.geometry.x = settings.editor.location.x - offset.x;
      vertex.geometry.y = settings.editor.location.y - offset.y;
    } else {
      vertex = graph.current!.createVertex(
        parentCell || graph.current!.getDefaultParent(),
        null,
        { ...settings, compId, virtualConfigPath },
        settings.editor.location.x - offset.x,
        settings.editor.location.y - offset.y,
        settings.dimensions.width,
        settings.dimensions.height,
        style
      );
      vertex['isVirtual'] = true;
      vertex['virtualComponentType'] = type;
      vertex['disableDelete'] = type === 'trigger';
      vertex.vertex = true;
      vertex.setConnectable(false);
      graph.current?.addCell(vertex, parentCell);
      addPorts(ports, vertex, graph.current!);
    }
  };

  /**
   * update and render all cells inb the graph
   */
  const renderGraph = (
    flow: Flow,
    parentCell: mxCell,
    options: {
      disableTrigger?: boolean;
      disableTails?: boolean;
      offset?: IPoint;
      locked?: boolean;
      cellDefaults?: IPropertyBag;
    } = {
      disableTrigger: false
    }
  ) => {
    const validation = ctx?.state.flowSummary!;
    const allComponents = getAllComponents(parentCell); // alls components in the graph
    const touchedComponents: Array<string> = []; // index of all component-keys rendered
    const groupComponents: Record<string, mxCell> = {}; // index of all group components
    const flowSettings = FlowLoader.getFlowTypeSettings(flow.type);
    const offset = options.offset || { x: 0, y: 0 };
    if (flowSettings?.mustTrigger && !options.disableTrigger) {
      renderVirtualComponent(
        'trigger',
        COMP_ID_TRIGGER,
        flow.config.editor?.trigger ? flow.config.editor['trigger'] : { location: DEFAULT_TRIGGER_LOCATION },
        parentCell,
        offset
      );
      touchedComponents.push(COMP_ID_TRIGGER);
    }

    if (flowSettings?.canReturn && options.disableTails !== true) {
      const ports = flow.getFlowPorts();

      mapRecord(ports, (portInfo, portKey) => {
        renderVirtualComponent(
          'tail',
          portKey,
          'editor' in portInfo ? portInfo.editor : { location: { x: 300, y: 300 } }, // else default value
          parentCell,
          offset
        );
        touchedComponents.push(portKey);

        return undefined;
      });
    }

    flow.forEach(flow.components, (component, owner, container, path) => {
      const settings = { ...component.definition, ...component.config } as IPropertyBag;
      const definition = flow.getComponentDefinition(component);
      const childComponentSettings = definition.childComponents;

      let vertex = findComponentRecursive(component.key);
      /**
       * Add manual expand/collapse.
       * Normal swimlaany the cell and render a new one
       * @todo, make a better way of detecting if cell is 'fake' collapsed
       *      also need to rerender if flow is changed
       */
      if (childComponentSettings?.preservePorts) {
        const collapsed = component.config.editor?.collapsed === true;
        if (vertex) {
          if ((collapsed && vertex.style !== 'vertex') || (!collapsed && vertex.style === 'vertex')) {
            graph.current?.removeCells([vertex], true);
            vertex = undefined;
          }
        }
      }

      /**
       * if vertex.structureHash changed, do a full rerender
       */
      if (vertex && vertex.structureHash && childComponentSettings) {
        const flowId = get(component.config, childComponentSettings.path);
        if (vertex.structureHash !== flowId) {
          graph.current?.removeCells([vertex], true);
          vertex = undefined;
        }
      }

      if (vertex) {
        /**
         * If vertex exists, update the location, and add to used usedComponents
         */
        // vertex.geometry.height = settings.size?.height || settings.dimensions.height;  // not these due to auto resizing
        // vertex.geometry.width = settings.size?.width || settings.dimensions.width;
        vertex.geometry.x = Number.parseInt(settings.editor?.location?.x, 10) - offset.x;
        vertex.geometry.y = Number.parseInt(settings.editor?.location?.y, 10) - offset.y;
        vertex.value = { ...settings, compId: component.key };
        touchedComponents.push(component.key);
      } else if (childComponentSettings) {
        const collapsed = component.config.editor?.collapsed === true;
        if (childComponentSettings?.preservePorts && collapsed && !childComponentSettings.internal) {
          vertex = createObject(
            { ...settings, ...(options.cellDefaults || {}) },
            component.key,
            owner.key in groupComponents ? groupComponents[owner.key] : parentCell,
            offset
          );
          vertex.isLocked = options.locked === true;
          vertex.isContentLocked = childComponentSettings.locked || options.locked === true;
          vertex.isEdgesBound = childComponentSettings.boundEdges === true;
          vertex.isCustomExpand = childComponentSettings.preservePorts;

          graph.current?.addCell(vertex, owner.key in groupComponents ? groupComponents[owner.key] : parentCell);
          addPorts(definition.ports, vertex, graph.current!, options.locked !== true);
        } else {
          const flowId = get(component.config, childComponentSettings.path);
          const entity =
            /**
             * @todo Implement When we have components that have other externals then flow
             */
            flowId && childComponentSettings.external === 'flow'
              ? (flow.getEntity(
                  childComponentSettings.external,
                  flowId,
                  childComponentSettings.path[childComponentSettings.path.length - 1]
                ) as Flow)
              : undefined;

          const bounds = entity
            ? getComponentBounds(entity, 50)
            : {
                upperLeft: { x: 0, y: 0 },
                lowerRight: {
                  x: settings.dimensions.width || childComponentSettings.dimensions.width,
                  y: settings.dimensions.height || childComponentSettings.dimensions.height
                }
              };

          const swimlaneOptions = {
            isLocked: options.locked === true,
            isContentLocked: childComponentSettings.locked === true || options.locked === true,
            isEdgesBound: childComponentSettings.boundEdges === true,
            isCustomExpand: childComponentSettings.preservePorts === true
          };
          vertex = createSwimlane(
            settings,
            component.key,
            parentCell,
            {
              ...offset,
              width: bounds.lowerRight.x - bounds.upperLeft.x,
              height: bounds.lowerRight.y - bounds.upperLeft.y
            },
            swimlaneOptions,
            childComponentSettings.preservePorts ? 'partial' : 'swimlane'
          );

          graph.current!.addCell(vertex, owner.key in groupComponents ? groupComponents[owner.key] : parentCell);
          graph.current?.orderCells(true, [vertex]);

          if (childComponentSettings?.internal) {
            groupComponents[component.key] = vertex;
          }

          addPorts(definition.ports, vertex, graph.current!);
          if (entity) {
            /**
             * Setting vertex.structureHash to the Id I fetched the entity with
             */
            vertex.structureHash = flowId;

            renderGraph(entity!, vertex, {
              disableTrigger: true,
              disableTails: true,
              offset: bounds.upperLeft,
              locked: vertex.isContentLocked,
              cellDefaults: { flowUuid: flowId }
            });
            renderEdges(entity!, vertex, { offset: bounds.upperLeft, locked: childComponentSettings.locked });
          }
          /**
           * Original swimlane needs to collapse, cant pass it by input settings, must call function
           * Not needed when we preservePorts, and use isCustomExpand
           */
          if (settings.editor.collapsed === true && !childComponentSettings?.preservePorts) {
            graph.current!.foldCells(true, false, [vertex]);
          }
        }
      } else {
        /**
         * Add normal component
         */
        vertex = createObject(
          { ...settings, ...(options.cellDefaults || {}) },
          component.key,
          owner.key in groupComponents ? groupComponents[owner.key] : parentCell,
          offset
        );
        graph.current?.addCell(vertex, owner.key in groupComponents ? groupComponents[owner.key] : parentCell);
        addPorts(component.definition.ports, vertex, graph.current!, options.locked !== true);
      }
      if (vertex) {
        updateCellErrors(vertex, component, validation);
      }

      return undefined;
    });

    const unUsedComponents = allComponents?.filter(
      component => !touchedComponents.find(key => component.value.compId === key)
    );
    if (unUsedComponents?.length) {
      graph.current?.removeCells(unUsedComponents, true);
    }
  };

  const updateCellErrors = (cell: mxCell, component: Component, validation: IValidationResult) => {
    const componentErrors = validation.messages.filter(
      message => message.component === component.key && message.port === undefined
    );
    if (componentErrors.length === 0) {
      if (cell.hasError) {
        cell.hasError = false;
        delete cell.errors;
      }
    } else {
      if (!cell.hasError) {
        cell.hasError = true;
      }
      cell.errors = componentErrors;
    }
  };

  /**
   * Render the connections between the components
   */
  const renderEdges = (flow: Flow, parentCell: mxCell, options?: { offset?: IPoint; locked?: boolean }) => {
    const allEdges = graph.current?.getAllEdges([parentCell]);
    const usedEdges: Array<string> = [];
    const validation = ctx?.state.flowSummary!;

    const renderFlowPipeline = (
      from: { key: string; port: string; waypoints?: Array<IPoint> },
      to: { key: string; port: string },
      errors: Array<IValidationMessage>
    ) => {
      const foundEdges = allEdges?.filter(aEdge => {
        try {
          const { source, target } = aEdge;
          const value =
            source &&
            target &&
            from.port === source.value.key &&
            from.key === source.parent.value.compId &&
            to.port === target.value.key &&
            to.key === target.parent.value.compId &&
            aEdge.flowKey === flow.key;

          return value;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('Invalid flow pipeline ', err, aEdge);

          return false;
        }
      });
      let edge: mxCell;
      const offset = options?.offset || { x: 0, y: 0 };
      const edgePoints: mxPoint[] = from.waypoints?.map(p => new mx.mxPoint(p.x - offset.x, p.y - offset.y)) || [];
      if (foundEdges?.length) {
        // foundEdges has double edges, one for each connection.
        edge = foundEdges[0];
        foundEdges.forEach(e => usedEdges.push(e.mxObjectId));
        /**
         * Change the error status. needed a hack to unset the style
         */
        const style = errors.length ? 'connectionError' : (undefined as any as string);
        if (style !== edge.getStyle()) {
          edge.setStyle(style);
        }
        foundEdges.forEach(e => {
          if (!_.isEqual(e.geometry.points, edgePoints)) {
            e.geometry.points = edgePoints;
          }
        });
      } else {
        edge = new mx.mxCell(null, new mx.mxGeometry(0, 0, 0, 0), errors.length ? 'connectionError' : undefined);
        edge.edge = true;
        edge.flowKey = flow.key;
        const fromComponent = findComponentRecursive(from.key)!;
        const toComponent = findComponentRecursive(to.key)!;
        const srcPort = graph.current
          ?.getChildCells(fromComponent, true, false)
          .filter(cell => cell.value.key === from.port)[0];
        const destPort = graph.current
          ?.getChildCells(toComponent, true, false)
          .filter(cell => cell.value.key === to.port)[0];

        if (!srcPort) {
          // eslint-disable-next-line no-console
          console.warn(`Warning on adding edge ${from.key}.${from.port} to ${to.key}.${to.port}`);
        }
        graph.current?.addEdge(edge, parentCell, srcPort, destPort);
        if (from.waypoints) {
          edge.geometry.points = edgePoints;
        }
      }
      if (errors.length) {
        edge.hasError = true;
        edge.errors = errors;
      } else {
        edge.hasError = false;
        edge.errors = undefined;
      }
    };

    const flowPorts = flow.getFlowPorts();

    flow.filterFlow(flow, toComponent => {
      // get all components connecting to my port
      const connectedPorts = flow.getConnectedPorts(toComponent);
      connectedPorts.forEach(c => {
        const [fromComp, fromPort, toPort] = c;
        const portConfig = fromComp.config.editor?.ports ? fromComp.config.editor?.ports[fromPort.key] : {};

        renderFlowPipeline(
          { key: fromComp.key, port: fromPort.key, waypoints: portConfig?.waypoints },
          { key: toComponent.key, port: toPort.key },
          validation.messages.filter(error => error.port === fromPort.key && error.component === fromComp.key)
        );
      });

      toComponent.getOutputPorts().forEach(port => {
        if (toComponent.config[port.key] in flowPorts) {
          const fromComp = toComponent.key;
          const fromPort = port.key;
          let toComp;
          let toPort;
          /**
           *  if we are inside the partial component
           *  we should connect to the parent 'passthrough' port
           */
          if (parentCell.isEdgesBound) {
            toComp = parentCell.value.compId;
            toPort = toComponent.config[port.key];
          } else {
            /**
             * else make an actual connection to the component
             * this might be a virtual port component
             */
            toComp = toComponent.config[port.key];
            toPort = 'input';
          }
          // connected to end port !

          const portConfig = toComponent.config.editor?.ports ? toComponent.config.editor?.ports[port.key] : {};
          renderFlowPipeline(
            { key: fromComp, port: fromPort, waypoints: parentCell.isEdgesBound ? undefined : portConfig?.waypoints },
            { key: toComp, port: toPort },
            validation.messages.filter(error => error.port === toComp && error.component === undefined)
          );
        }
      });

      return undefined;
    });
    if (flow.config.trigger) {
      const portConfig = flow.config.editor?.trigger || {};
      const triggerComponent = flow.getComponent(flow.config.trigger);
      if (triggerComponent) {
        if (parentCell.isEdgesBound) {
          renderFlowPipeline(
            { key: parentCell.value.compId, port: 'input' },
            { key: triggerComponent.key, port: 'input' },
            validation.messages.filter(error => error.port === 'input' && error.component === undefined)
          );
        } else {
          renderFlowPipeline(
            { key: COMP_ID_TRIGGER, port: 'next', waypoints: portConfig?.waypoints },
            { key: triggerComponent.key, port: 'input' },
            validation.messages.filter(error => error.port === 'input' && error.component === undefined)
          );
        }
      }
    }

    const unUsedEdges = allEdges?.filter(
      edge =>
        (flow.key === edge.flowKey || edge.flowKey === undefined) && !usedEdges.find(used => used === edge.mxObjectId)
    );
    if (unUsedEdges?.length) {
      graph.current?.removeCells(unUsedEdges, true);
    }
  };

  /**
   * Drop function called when component is dropped from the tool panel
   * @param e React.DragEvent
   */
  const onNodeDrop = (e: any) => {
    e.preventDefault();
    const point = graph.current!.getPointForEvent(e, true);
    const type = e.dataTransfer.getData('componentType');
    try {
      // if we have a dragOffset, use it to modify insertion point
      const offset = JSON.parse(e.dataTransfer.getData('dragOffset'));
      point.x -= Number.parseInt(offset.x, 10);
      point.y -= Number.parseInt(offset.y, 10);
    } catch (ex) {
      // ignore error, was invalid dragOffset
    }

    /**
     * Detect drop of a port component
     */
    if (type === OUTPUT_PORT_COMPONENT_ID) {
      ctx?.dispatch({
        type: FlowActionTypes.CREATE_FLOW_PORT,
        payload: {
          key: `port_${uuidv4().replace(/-/g, '_')}`,
          position: point,
          settings: {}
        }
      });
    } else {
      addFlowComponent(type, point);
    }
  };

  /**
   * Add a new component to the flow, Only used in onNodeDrop()
   * @param componentType
   * @param dropPoint
   */
  const addFlowComponent = (componentType: string, dropPoint: mxPoint) => {
    const compDTO = ComponentFactory.get(componentType);

    if (compDTO && graph.current) {
      const isGroup = compDTO?.groupPaths !== undefined;

      const group = graph.current.getCellAt(dropPoint.x, dropPoint.y);
      if (group && group.isGroup && group.isLocked !== true) {
        dropPoint = new mx.mxPoint(dropPoint.x - group.geometry.x, dropPoint.y - group.geometry.y);
      }

      const compId = `${componentType}_${uuidv4().replace(/-/g, '_')}`;
      const componentConfig = {
        ...(ComponentFactory.getDefaultComponentConfigValues(componentType) || {}),
        ...{
          type: componentType,
          editor: { location: { ...dropPoint }, size: isGroup ? { width: 300, height: 200 } : undefined }
        }
      };
      if (ctx) {
        ctx.dispatch({
          type: FlowActionTypes.ADD_COMPONENT,
          payload: {
            componentId: compId,
            groupComponentId: group?.isGroup && group.isLocked !== true ? group?.value?.compId : undefined,
            componentConfig
          }
        });
      }
    }
  };

  const hasErrors = ctx?.state.reducerErrors?.length || renderError;
  setSelection();

  return (
    <>
      <Box style={{ position: 'relative', overflow: 'hidden', display: hasErrors ? 'none' : undefined }}>
        <Prompt
          when={isEditable && !isEqual(data?.getFlow.schema, ctx?.state.flowSchema)}
          message={(location, action) => `Changes that you made will not be saved.`}
        />
        <Box
          onDragOver={e => e.preventDefault()}
          onDrop={onNodeDrop}
          onWheel={throttle(graph.current?.lazyZoom, 50)}
          style={{
            height: 'calc(100vh - 167px)',
            width: '100vw',
            overflow: 'hidden',
            marginTop: '96px'
          }}
          ref={editorContainer}
        />
        <FlowPanel isEditable={isEditable} />
      </Box>
      {hasErrors && (
        <Box style={{ marginTop: '100px' }}>
          <FlexLayout alignItems="center" flexDirection="column">
            <Box marginBottom="24px">
              <H1>:-(</H1>
              <H3>Sorry, something went wrong. Please refresh the page. </H3>
              <>
                {process.env.NODE_ENV !== 'production'
                  ? `${ctx?.state.reducerErrors?.join('. ')} ${renderError || ''}`
                  : ''}
              </>
            </Box>
            <Button
              variant="contained"
              onClick={() => {
                setRenderError(undefined);
                ctx?.dispatch({
                  type: FlowActionTypes.CLEAR_ERROR
                });
              }}
            >
              Refresh the page
            </Button>
          </FlexLayout>
        </Box>
      )}
    </>
  );
};

export default FlowEditor;
